import { useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../components/ui'
import { modeAwareEndpoint } from '../utils/cockpitMode'
import './Services.css'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface Ticket {
  id: number
  meta: {
    subject?: string
    sender?: { name?: string }
  }
  labels: string[]
  status: string
  priority?: string
  created_at: string
  additional_attributes?: { org_slug?: string; created_by?: string }
}

type TicketCategory = 'bug' | 'feedback' | 'support'

interface Props {
  organization: Organization
}

function statusColor(status: string): string {
  switch (status) {
    case 'open': return '#1a7340'
    case 'resolved': return '#005bbb'
    case 'pending': return '#c47f00'
    default: return '#666'
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'open': return 'Offen'
    case 'resolved': return 'Gelöst'
    case 'pending': return 'Wartend'
    default: return status
  }
}

function categoryLabel(cat: TicketCategory): string {
  switch (cat) {
    case 'bug': return 'Bug melden'
    case 'feedback': return 'Feedback geben'
    case 'support': return 'Support anfragen'
  }
}

function categoryEmoji(cat: TicketCategory): string {
  switch (cat) {
    case 'bug': return '🐛'
    case 'feedback': return '💬'
    case 'support': return '🆘'
  }
}

function Badge({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 12, fontWeight: 600, background: bg, color,
    }}>{label}</span>
  )
}

// TQ #6279: Chatwoot liefert created_at oft als Unix-Timestamp in SEKUNDEN
// (number), JavaScript Date erwartet Millisekunden. Resultat war '21.01.1970'
// (= 8ms nach Epoch). Heuristik: numerischer Wert kleiner als 10^12 → Sekunden.
function parseTimestamp(ts: string | number | null | undefined): Date | null {
  if (ts === null || ts === undefined || ts === '') return null
  if (typeof ts === 'number') {
    const ms = ts < 1e12 ? ts * 1000 : ts
    const d = new Date(ms)
    return isNaN(d.getTime()) ? null : d
  }
  // String: koennte ISO oder numerisch sein
  const asNum = Number(ts)
  if (Number.isFinite(asNum) && /^\d+(\.\d+)?$/.test(ts.trim())) {
    const ms = asNum < 1e12 ? asNum * 1000 : asNum
    const d = new Date(ms)
    return isNaN(d.getTime()) ? null : d
  }
  const d = new Date(ts)
  return isNaN(d.getTime()) ? null : d
}

function formatDate(ts: string | number | null | undefined): string {
  const d = parseTimestamp(ts)
  if (!d) return '—'
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatDateTime(ts: string | number | null | undefined): string {
  const d = parseTimestamp(ts)
  if (!d) return '—'
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// TQ #6277: Chatwoot-Message-Shape (subset). Backend reicht payload via
// json.RawMessage durch, hier nur die Felder die wir tatsaechlich rendern.
interface TicketMessage {
  id: number
  content?: string | null
  content_type?: string
  message_type?: number  // 0=incoming, 1=outgoing, 2=activity
  private?: boolean
  created_at?: number | string
  sender?: {
    id?: number
    name?: string
    type?: string
    avatar_url?: string
  } | null
  attachments?: Array<{
    id?: number
    file_url?: string
    file_type?: string
    file_size?: number
  }> | null
}

export default function Tickets({ organization }: Props) {
  const { showToast } = useToast()
  const { api: keycloakApi } = useAuth()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [creating, setCreating] = useState<TicketCategory | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState({ subject: '', message: '' })
  // TQ #6277: Pro Ticket-ID Thread-State (Messages + Loading + Error).
  const [threads, setThreads] = useState<Record<number, {
    loading: boolean
    error: string | null
    messages: TicketMessage[]
  }>>({})

  // TQ #6929 Refactor Wave 3: im Personal-Mode (with.it-sce.de) laufen Tickets
  // über /api/me/tickets (Chatwoot q=email Filter, LIVE seit 485fd6e; Create via
  // #6934), im Org-Mode über /api/org/<slug>/tickets. modeAwareEndpoint (#7348).
  const ticketsBase = modeAwareEndpoint('tickets', organization)

  useEffect(() => {
    loadTickets()
  }, [organization.slug])

  const loadTickets = async () => {
    try {
      setLoading(true)
      const response = await keycloakApi!.get(ticketsBase)
      const raw = response.data.tickets ?? []
      setTickets(Array.isArray(raw) ? raw : [])
    } catch {
      showToast('error', 'Tickets konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }

  const loadThread = async (ticketId: number) => {
    setThreads(prev => ({
      ...prev,
      [ticketId]: { loading: true, error: null, messages: prev[ticketId]?.messages ?? [] },
    }))
    try {
      const resp = await keycloakApi!.get(`${ticketsBase}/${ticketId}/messages`)
      const raw = resp.data.messages ?? []
      const messages = Array.isArray(raw) ? (raw as TicketMessage[]) : []
      setThreads(prev => ({
        ...prev,
        [ticketId]: { loading: false, error: null, messages },
      }))
    } catch {
      setThreads(prev => ({
        ...prev,
        [ticketId]: {
          loading: false,
          error: 'Konversation konnte nicht geladen werden.',
          messages: prev[ticketId]?.messages ?? [],
        },
      }))
    }
  }

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
        // Beim Aufklappen Thread laden falls noch nicht da.
        if (!threads[id] || (!threads[id].loading && threads[id].messages.length === 0 && !threads[id].error)) {
          void loadThread(id)
        }
      }
      return next
    })
  }

  const submitTicket = async () => {
    if (!form.subject.trim() || !form.message.trim() || !creating) return
    setSubmitting(true)
    try {
      await keycloakApi!.post(ticketsBase, {
        subject: `[${creating.toUpperCase()}] ${form.subject}`,
        message: form.message,
        priority: creating === 'bug' ? 'high' : 'medium',
      })
      showToast('success', 'Ticket erstellt')
      setCreating(null)
      setForm({ subject: '', message: '' })
      await loadTickets()
    } catch {
      showToast('error', 'Ticket konnte nicht erstellt werden')
    } finally {
      setSubmitting(false)
    }
  }

  const openCount = tickets.filter(t => t.status === 'open').length

  return (
    <div style={{ padding: '32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Support & Feedback</h2>
          {!loading && tickets.length > 0 && (
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: 14 }}>
              {openCount} offen · {tickets.length} gesamt
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['bug', 'feedback', 'support'] as TicketCategory[]).map(cat => (
            <button
              key={cat}
              onClick={() => { setCreating(cat); setForm({ subject: '', message: '' }) }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd',
                background: creating === cat ? '#f0f4ff' : '#fff',
                borderColor: creating === cat ? '#4f46e5' : '#ddd',
                color: creating === cat ? '#4f46e5' : '#444',
                cursor: 'pointer', fontSize: 13, fontWeight: 500,
              }}
            >
              <span>{categoryEmoji(cat)}</span>
              {categoryLabel(cat)}
            </button>
          ))}
        </div>
      </div>

      {creating && (
        <div style={{
          background: '#f8f8fc', border: '1px solid #e0e0f0', borderRadius: 12,
          padding: '20px', marginBottom: 24,
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>
            {categoryEmoji(creating)} {categoryLabel(creating)}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              placeholder="Betreff"
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              style={{
                padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd',
                fontSize: 14, outline: 'none',
              }}
            />
            <textarea
              placeholder="Beschreibung"
              value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              rows={4}
              style={{
                padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd',
                fontSize: 14, resize: 'vertical', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setCreating(null)}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd',
                  background: '#fff', cursor: 'pointer', fontSize: 13,
                }}
              >Abbrechen</button>
              <button
                onClick={submitTicket}
                disabled={submitting || !form.subject.trim() || !form.message.trim()}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: submitting ? '#aaa' : '#4f46e5', color: '#fff',
                  cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >{submitting ? 'Wird gesendet…' : 'Senden'}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: '#888' }}>Tickets werden geladen…</p>
      ) : tickets.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '64px 24px',
          background: '#fafafa', borderRadius: 12, border: '1px dashed #ddd',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎫</div>
          <p style={{ color: '#888', margin: 0 }}>Noch keine Tickets vorhanden</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tickets.map(ticket => {
            const isOpen = expanded.has(ticket.id)
            const subject = ticket.meta?.subject ?? `Ticket #${ticket.id}`
            const createdBy = ticket.additional_attributes?.created_by ?? '—'
            return (
              <div key={ticket.id} style={{
                border: '1px solid #eee', borderRadius: 10, overflow: 'hidden',
              }}>
                <div
                  onClick={() => toggleExpand(ticket.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 16px', cursor: 'pointer',
                    background: isOpen ? '#f5f5ff' : '#fff',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ color: '#aaa', fontSize: 12, minWidth: 48 }}>#{ticket.id}</span>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{subject}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Badge
                      label={statusLabel(ticket.status)}
                      bg={ticket.status === 'open' ? '#e6f4ea' : ticket.status === 'resolved' ? '#e8f0fe' : '#fff8e1'}
                      color={statusColor(ticket.status)}
                    />
                    <span style={{ color: '#bbb', fontSize: 12 }}>{formatDate(ticket.created_at)}</span>
                    <span style={{ color: '#9ca3af', transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'none', display: 'inline-block', lineHeight: 1 }}>›</span>
                  </div>
                </div>
                {isOpen && (
                  <div style={{ padding: '16px', borderTop: '1px solid #eee', background: '#fafafa', fontSize: 14 }}>
                    <div style={{ display: 'flex', gap: 24, color: '#666', marginBottom: 12, flexWrap: 'wrap' }}>
                      <span><strong>Erstellt von:</strong> {createdBy}</span>
                      <span><strong>Priorität:</strong> {ticket.priority ?? 'medium'}</span>
                      <span><strong>Labels:</strong> {ticket.labels.join(', ') || '—'}</span>
                    </div>
                    {(() => {
                      const t = threads[ticket.id]
                      if (!t || t.loading) {
                        return <p style={{ color: '#888', margin: 0, fontStyle: 'italic' }}>Konversation wird geladen …</p>
                      }
                      if (t.error) {
                        return <p style={{ color: '#c0392b', margin: 0 }}>{t.error}</p>
                      }
                      const visible = t.messages.filter(m => !m.private && m.message_type !== 2)
                      if (visible.length === 0) {
                        return <p style={{ color: '#888', margin: 0, fontStyle: 'italic' }}>Noch keine Nachrichten in dieser Konversation.</p>
                      }
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {visible.map(m => {
                            const outgoing = m.message_type === 1
                            const senderName = m.sender?.name || (outgoing ? 'IT-SCE Support' : 'Sie')
                            return (
                              <div
                                key={m.id}
                                style={{
                                  alignSelf: outgoing ? 'flex-start' : 'flex-end',
                                  maxWidth: '85%',
                                  background: outgoing ? '#ffffff' : '#e8f4fd',
                                  border: '1px solid ' + (outgoing ? '#e5e7eb' : '#bee3f8'),
                                  borderRadius: 10,
                                  padding: '10px 12px',
                                  boxShadow: '0 1px 1px rgba(0,0,0,0.04)',
                                }}
                              >
                                <div style={{
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                                  gap: 8, marginBottom: 4, fontSize: 12, color: '#555',
                                }}>
                                  <strong style={{ fontSize: 13 }}>{senderName}</strong>
                                  <span>{formatDateTime(m.created_at)}</span>
                                </div>
                                {m.content && (
                                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content}</div>
                                )}
                                {m.attachments && m.attachments.length > 0 && (
                                  <div style={{ marginTop: 6, fontSize: 12 }}>
                                    {m.attachments.map(a => (
                                      <div key={a.id ?? a.file_url}>
                                        📎 {a.file_url
                                          ? <a href={a.file_url} target="_blank" rel="noopener noreferrer">Anhang öffnen</a>
                                          : 'Anhang'}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )
                    })()}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
